import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setTestType, getTestType, type TestParams } from 'store/slices/test';
import { TestType } from 'types/testType';
import { URLParamKey } from 'constants/urlParams';
import { sentryLogMessage } from 'services/sentryLogger';
import { SENTRY_EVENT_TYPES } from 'constants/sentryEvents';
import { detectHmo } from 'utils/hmoDetection';
import { HMO } from 'constants/hmo';

const DEFAULT_TEST_TYPE = TestType.FOB;

/**
 * Gets a URL search parameter value with case-insensitive matching
 * @param queryParams - URLSearchParams object
 * @param targetKey - The parameter key to find (will be normalized to lowercase)
 * @returns The parameter value or null if not found
 */
const getParamCaseInsensitive = (
  queryParams: URLSearchParams,
  targetKey: string
): string | null => {
  const normalizedTarget = targetKey.toLowerCase();
  
  // Iterate through all entries to find case-insensitive match
  for (const [key, value] of queryParams.entries()) {
    if (key.toLowerCase() === normalizedTarget) {
      return value;
    }
  }
  
  return null;
};

/**
 * Validates and normalizes a testType string to a TestType enum value
 * @param value - The raw string value from URL
 * @returns The validated TestType enum value or default
 */
const validateTestType = (value: string | null): TestType => {
  if (!value) {
    return DEFAULT_TEST_TYPE;
  }

  const normalized = value.toLowerCase().trim();

  // Check against all TestType enum values
  const testTypeValues = Object.values(TestType) as string[];
  
  if (testTypeValues.includes(normalized)) {
    return normalized as TestType;
  }

  console.warn(
    `[useTestParams] Invalid testType value: "${value}", using default: ${DEFAULT_TEST_TYPE}`
  );
  return DEFAULT_TEST_TYPE;
};

/**
 * Extracts and validates test parameters from URL search string
 * Supports case-insensitive parameter names (e.g., testType, testtype, TESTTYPE)
 * @param search - URL search string (e.g., "?testType=hpylori&sessionId=abc123")
 * @returns Object with testType and sessionId
 */
const parseTestParamsFromURL = (search: string): TestParams => {
  const queryParams = new URLSearchParams(search);

  // Extract testType (case-insensitive - supports testType, testtype, TESTTYPE, etc.)
  const testTypeParam = getParamCaseInsensitive(queryParams, URLParamKey.TEST_TYPE);
  const testType = validateTestType(testTypeParam);

  // Extract sessionId ( case-insensitive - supports sessionId, sessionid, SESSIONID, etc.)
  const sessionId = getParamCaseInsensitive(queryParams, URLParamKey.SESSION_ID);

  return {
    testType,
    sessionId,
  };
};

/**
 * Custom hook for managing test parameters from URL
 * - Parses testType and sessionId from URL search params
 * - Syncs them to Redux store
 * - Provides access to current test params
 * 
 * @returns Object containing testType and sessionId
 */
const useTestParams = () => {
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const lastDispatchedParams = useRef<string | null>(null);
  const storeParams = useAppSelector(getTestType);

  // Parse URL params and sync to Redux
  useEffect(() => {
    const urlParams = parseTestParamsFromURL(search);
    const paramsKey = `${urlParams.testType}-${urlParams.sessionId || 'null'}`;

    if (paramsKey !== lastDispatchedParams.current) {
      lastDispatchedParams.current = paramsKey;
      dispatch(setTestType(urlParams));
      
      // Log session start event for clinical trial (when sessionId is present)
      if (urlParams.sessionId) {
        sentryLogMessage({
          message: 'Session started - QR scanned and logged in',
          level: 'info',
          tags: {
            component: 'useTestParams',
            category: 'session_start',
            testType: urlParams.testType,
            event_type: SENTRY_EVENT_TYPES.SESSION_START,
          },
          extra: {
            sessionId: urlParams.sessionId,
            timestamp: new Date().toISOString(),
          },
        });
      }
    }
  }, [search, dispatch]);

  // Check if should move directly to camera step (case-insensitive)
  const shouldMoveToCamera = useCallback((): boolean => {
    const queryParams = new URLSearchParams(search);
    return getParamCaseInsensitive(queryParams, URLParamKey.MOVE_TO_CAMERA) === 'true';
  }, [search]);

  return {
    testType: storeParams.testType,
    sessionId: storeParams.sessionId,
    shouldMoveToCamera,
  };
};

export default useTestParams;

